import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import MarketingShell from "../MarketingShell";
import SEOHead from "../../components/SEOHead";
import { useOpeningHours, useTourPricing, openingHoursSchema, formatPrice } from "../useBusinessInfo";
import "./underground-tours.css";

/**
 * Underground Tours — redesigned public page (route: /underground-tours).
 *
 * Visual/interaction ground truth: design-ref/public/Underground Tours.html
 * Functional ground truth: frontend/src/pages/UndergroundToursPage.js —
 * all three public settings fetches and every conditional (Mon/Fri
 * restriction, override note, Closed Tuesdays, tour-booking gating of
 * /book-tour CTAs) are preserved.
 */
const RUndergroundToursPage = () => {
  const [tourBookingEnabled, setTourBookingEnabled] = useState(true);
  const [tourSchedule, setTourSchedule] = useState({ restrict_monday_friday: true });
  const openingHours = useOpeningHours();
  const pricing = useTourPricing();

  useEffect(() => {
    checkTourBookingStatus();
    fetchTourSchedule();
  }, []);

  const checkTourBookingStatus = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings/features/public`);
      if (response.ok) {
        const data = await response.json();
        setTourBookingEnabled(!data.store_maintenance_mode); // Assume tours available when store not in maintenance
      }
    } catch (error) {
      console.error('Error checking tour booking status:', error);
    }
  };

  const fetchTourSchedule = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings/tour-schedule/public`);
      if (response.ok) {
        const data = await response.json();
        setTourSchedule(data);
      }
    } catch (error) {
      console.error('Error fetching tour schedule:', error);
    }
  };

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "TouristAttraction",
    "name": "Underground Opal Mine Tours - Umoona",
    "description": "Authentic underground opal mine tours in Coober Pedy. Experience real mining tunnels, learn about opal formation, and discover Australian mining heritage.",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "14 Hutchison St",
      "addressLocality": "Coober Pedy",
      "addressRegion": "SA",
      "postalCode": "5723",
      "addressCountry": "AU"
    },
    ...openingHoursSchema(openingHours),
    "priceRange": `${formatPrice(Math.min(pricing.adult, pricing.child), { cents: true })}-${formatPrice(Math.max(pricing.adult, pricing.child), { cents: true })}`,
    "url": "https://umoonaopalmine.com.au/underground-tours"
  };

  const galleryImages = [
    "/design/assets/tour-2.jpg",
    "/design/assets/tour-3.jpg",
    "/design/assets/tour-4.jpg",
    "/design/assets/tour-5.jpg",
    "/design/assets/photo-minetour.jpg",
    "/design/assets/museum-3.jpg"
  ];

  return (
    <MarketingShell pageClass="pg-underground-tours">
      <SEOHead
        title="Underground Opal Mine Tours | Umoona Opal Mine Coober Pedy"
        description="Experience real underground opal mine tours in Coober Pedy. Descend into Umoona Mine, discover opal mining history, and see where the world's finest opals are found."
        keywords="underground tours, opal mine tours, Coober Pedy tours, mining experience, authentic mining, underground attractions, South Australia tours"
        canonical="/underground-tours"
        ogImage="/og-umoona.jpg"
        schemaData={schemaData}
      />

      {/* HERO */}
      <div className="u-hero">
        <img
          className="u-hero__img"
          src="/design/assets/tour-1.jpg"
          alt="Underground Opal Mine Tours at Umoona"
        />
        <div className="u-hero__scrim" />
        <div className="u-hero__inner">
          <div className="u-hero__eyebrow">Since the 1920s · Authentic underground</div>
          <h1 className="u-hero__title">Guided Underground Opal Mine Tours</h1>
          <p className="u-hero__sub">
            Umoona Opal Mine was first worked in the 1920s. Today we take you through this genuine
            Coober Pedy mine to see — and understand — how opal is formed and mined, deep beneath
            the desert.
          </p>
          {tourBookingEnabled && (
            <div className="ut-hero__cta">
              <Link to="/book-tour" className="u-btn u-btn--primary">Pre-Book Online Now →</Link>
            </div>
          )}
        </div>
      </div>

      {/* TOUR SCHEDULE & PRICING */}
      <section className="u-sectpad u-sect-deep">
        <div className="u-eyebrow">🕒 Tour schedule &amp; pricing</div>
        <h2 className="u-h2 ut-h2-tight">10am, 2pm &amp; 4pm — daily</h2>
        <p className="ut-lead">
          See our shop staff 10 minutes before tour time to book a ticket, or pre-book online.
          Limited spaces available for each tour time.
        </p>

        {(tourSchedule.restrict_monday_friday || openingHours.closed_on_tuesdays) && (
          <div className="ut-notices">
            {tourSchedule.restrict_monday_friday && (
              <div className="ut-notice ut-notice--warn">
                EXCEPT MONDAYS &amp; FRIDAYS when only the 4pm tour runs
              </div>
            )}
            {tourSchedule.restrict_monday_friday && tourSchedule.overrides?.some(o => o.enabled) && (
              <div className="ut-notice ut-notice--info">
                During certain periods, additional tour times may be available on Mondays and
                Fridays. Select your date to see available times.
              </div>
            )}
            {openingHours.closed_on_tuesdays && (
              <div className="ut-notice ut-notice--alert">Closed on Tuesdays</div>
            )}
          </div>
        )}

        <div className="u-grid2">
          <div className="ut-card">
            <div className="ut-card__kicker">ADULTS</div>
            <div className="ut-card__title">{formatPrice(pricing.adult, { cents: true })}</div>
            <p className="ut-card__text">Ages 16 and over. Full guided tour of the original mine workings.</p>
          </div>
          <div className="ut-card">
            <div className="ut-card__kicker ut-card__kicker--teal">CHILDREN</div>
            <div className="ut-card__title">{formatPrice(pricing.child, { cents: true })}</div>
            <p className="ut-card__text">Under 16 years. Under 5s tour free. Supervision required.</p>
          </div>
        </div>

        {tourBookingEnabled && (
          <div className="ut-bookrow">
            <Link to="/book-tour" className="u-btn u-btn--primary">Pre-Book Online Now →</Link>
          </div>
        )}
      </section>

      {/* WHAT YOU'LL EXPERIENCE */}
      <section className="u-sectpad u-sect-test">
        <div className="u-eyebrow">What you'll experience</div>
        <h2 className="u-h2">Real workings, real stories</h2>
        <div className="u-grid3">
          <div className="ut-card">
            <div className="ut-card__kicker">ORIGINAL WORKINGS</div>
            <div className="ut-card__title">Authentic tunnels</div>
            <p className="ut-card__text">Explore genuine tunnels where opal was actually mined decades ago.</p>
          </div>
          <div className="ut-card">
            <div className="ut-card__kicker ut-card__kicker--teal">EXPERT GUIDES</div>
            <div className="ut-card__title">Local knowledge</div>
            <p className="ut-card__text">Learn from locals with decades of mining experience and fascinating stories.</p>
          </div>
          <div className="ut-card">
            <div className="ut-card__kicker ut-card__kicker--cyan">22–24°C</div>
            <div className="ut-card__title">Comfortable underground</div>
            <p className="ut-card__text">Enjoy a consistent, cool temperature year-round, whatever the desert does.</p>
          </div>
        </div>
      </section>

      {/* GALLERY */}
      <section className="u-sectpad u-sect-deep">
        <div className="u-eyebrow">Gallery</div>
        <h2 className="u-h2">Inside the mine</h2>
        <div className="u-grid3 ut-gallery">
          {galleryImages.map((image, index) => (
            <div key={index} className="ut-gallery__tile">
              <img src={image} alt={`Underground mine tour experience ${index + 1}`} loading="lazy" decoding="async" />
            </div>
          ))}
        </div>
      </section>

      {/* IMPORTANT INFORMATION */}
      <section className="u-sectpad u-sect-test">
        <div className="u-eyebrow">Important information</div>
        <h2 className="u-h2">Plan for your descent</h2>
        <div className="u-grid2">
          <div className="ut-card">
            <div className="ut-card__kicker">WHAT TO BRING</div>
            <div className="ut-card__title">Come prepared</div>
            <p className="ut-card__text">
              Comfortable closed-toe shoes recommended · a light jacket or sweater · a camera · and
              a sense of adventure!
            </p>
          </div>
          <div className="ut-card">
            <div className="ut-card__kicker ut-card__kicker--teal">BOOKING POLICY</div>
            <div className="ut-card__title">Good to know</div>
            <p className="ut-card__text">
              Advance booking recommended · group discounts for 10+ · all ages welcome (child
              supervision required) · there are 15 steep steps to enter the mine.
            </p>
          </div>
        </div>
        <div className="ut-access">
          <strong>Accessibility:</strong> Our underground mine tour is not wheelchair accessible
          due to 15 steep steps and uneven underground terrain. Public areas and the museum are
          wheelchair accessible.
        </div>
      </section>

      {/* CTA */}
      <section className="u-sectpad u-sect-cta">
        <div className="u-ctacenter">
          <h2 className="u-ctacenter__title">Ready to explore underground?</h2>
          <p className="u-ctacenter__sub">
            Book your tour now, or add the free museum for the complete experience.
          </p>
          <div className="u-ctacenter__btns">
            {tourBookingEnabled && (
              <Link to="/book-tour" className="u-btn u-btn--primary">Book Your Tour Now</Link>
            )}
            <Link to="/free-museum" className="u-btn u-btn--ghost">Visit Free Museum</Link>
          </div>
        </div>
      </section>
    </MarketingShell>
  );
};

export default RUndergroundToursPage;
